import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

/**
 * Pads a Hebrew Strong's number to 4 digits for STEPBible e_strong queries.
 * H216 → H0216, H1234 → H1234, G123 → G123 (Greek unchanged)
 */
function padStrongsForSTEP(key) {
  if (!key) return key;
  const m = key?.match(/^(H)(\d+)([A-Za-z]*)$/);
  if (m) {
    const padded = m?.[2]?.padStart(4, '0');
    return `${m?.[1]}${padded}${m?.[3]}`;
  }
  return key;
}

/**
 * Strips leading zeros from a Hebrew Strong's number for Traditional queries.
 * H0216 → H216, H1234 → H1234, G0123 → G0123 (Greek unchanged)
 */
function stripLeadingZerosForTraditional(key) {
  if (!key) return key;
  const m = key?.match(/^(H)0*(\d+)([A-Za-z]*)$/);
  if (m) {
    return `${m?.[1]}${m?.[2]}${m?.[3]}`;
  }
  return key;
}

/**
 * Parses a raw s_strong value into:
 *   - eStrongKey: the base number used to query e_strong in the lexicon
 *   - disambigSuffix: the uppercase letter suffix that was stripped (if any)
 *   - instanceSuffix: the _A/_B instance suffix that was stripped (if any)
 */
function parseStrongsNumber(raw) {
  if (!raw) return { eStrongKey: '', disambigSuffix: '', instanceSuffix: '' };

  let s = raw?.trim();
  let instanceSuffix = '';

  const instanceMatch = s?.match(/^([HG]\d+[A-Za-z]*)_([A-Za-z]+)$/);
  if (instanceMatch) {
    s = instanceMatch?.[1];
    instanceSuffix = instanceMatch?.[2];
  }

  const upperSuffixMatch = s?.match(/^([HG]\d+)([A-Z])$/);
  if (upperSuffixMatch) {
    return {
      eStrongKey: upperSuffixMatch?.[1],
      disambigSuffix: upperSuffixMatch?.[2],
      instanceSuffix,
    };
  }

  return { eStrongKey: s, disambigSuffix: '', instanceSuffix };
}

function getSuffixExplanation(suffix, isHebrew) {
  if (!suffix) return null;
  return `"${suffix}" is a disambiguation suffix added by STEPBible's Extended Strong's system to distinguish multiple distinct ${isHebrew ? 'Hebrew' : 'Greek'} individuals, places, or word senses that share the same root Strong's number. The base number is used for the lexicon lookup; the suffix identifies the specific person or variant in context.`;
}

// ── Book abbreviation → full name map ──
const BOOK_ABBREV_MAP = {
  'Gen': 'Genesis', 'Exo': 'Exodus', 'Exod': 'Exodus', 'Lev': 'Leviticus',
  'Num': 'Numbers', 'Deu': 'Deuteronomy', 'Deut': 'Deuteronomy',
  'Jos': 'Joshua', 'Josh': 'Joshua', 'Jdg': 'Judges', 'Judg': 'Judges',
  'Rut': 'Ruth', 'Ruth': 'Ruth',
  '1Sa': '1 Samuel', '1Sam': '1 Samuel', '2Sa': '2 Samuel', '2Sam': '2 Samuel',
  '1Ki': '1 Kings', '1Kgs': '1 Kings', '2Ki': '2 Kings', '2Kgs': '2 Kings',
  '1Ch': '1 Chronicles', '1Chr': '1 Chronicles', '2Ch': '2 Chronicles', '2Chr': '2 Chronicles',
  'Ezr': 'Ezra', 'Neh': 'Nehemiah', 'Est': 'Esther', 'Esth': 'Esther',
  'Job': 'Job', 'Psa': 'Psalms', 'Ps': 'Psalms',
  'Pro': 'Proverbs', 'Prov': 'Proverbs', 'Ecc': 'Ecclesiastes', 'Eccl': 'Ecclesiastes',
  'Sng': 'Song of Solomon', 'Song': 'Song of Solomon',
  'Isa': 'Isaiah', 'Jer': 'Jeremiah', 'Lam': 'Lamentations',
  'Ezk': 'Ezekiel', 'Ezek': 'Ezekiel', 'Dan': 'Daniel',
  'Hos': 'Hosea', 'Joe': 'Joel', 'Joel': 'Joel', 'Amo': 'Amos', 'Amos': 'Amos',
  'Oba': 'Obadiah', 'Obad': 'Obadiah', 'Jon': 'Jonah', 'Jonah': 'Jonah',
  'Mic': 'Micah', 'Nah': 'Nahum', 'Hab': 'Habakkuk', 'Zep': 'Zephaniah', 'Zeph': 'Zephaniah',
  'Hag': 'Haggai', 'Zec': 'Zechariah', 'Zech': 'Zechariah', 'Mal': 'Malachi',
  'Mat': 'Matthew', 'Matt': 'Matthew', 'Mrk': 'Mark', 'Mark': 'Mark',
  'Luk': 'Luke', 'Luke': 'Luke', 'Jhn': 'John', 'John': 'John',
  'Act': 'Acts', 'Acts': 'Acts', 'Rom': 'Romans',
  '1Co': '1 Corinthians', '1Cor': '1 Corinthians',
  '2Co': '2 Corinthians', '2Cor': '2 Corinthians',
  'Gal': 'Galatians', 'Eph': 'Ephesians', 'Php': 'Philippians', 'Phil': 'Philippians',
  'Col': 'Colossians',
  '1Th': '1 Thessalonians', '1Thess': '1 Thessalonians',
  '2Th': '2 Thessalonians', '2Thess': '2 Thessalonians',
  '1Ti': '1 Timothy', '1Tim': '1 Timothy',
  '2Ti': '2 Timothy', '2Tim': '2 Timothy',
  'Tit': 'Titus', 'Phm': 'Philemon', 'Phlm': 'Philemon',
  'Heb': 'Hebrews', 'Jas': 'James',
  '1Pe': '1 Peter', '1Pet': '1 Peter',
  '2Pe': '2 Peter', '2Pet': '2 Peter',
  '1Jn': '1 John', '1John': '1 John',
  '2Jn': '2 John', '2John': '2 John',
  '3Jn': '3 John', '3John': '3 John',
  'Jud': 'Jude', 'Jude': 'Jude', 'Rev': 'Revelation',
};

function resolveBookName(abbrev) {
  if (!abbrev) return null;
  if (BOOK_ABBREV_MAP?.[abbrev]) return BOOK_ABBREV_MAP?.[abbrev];
  const lower = abbrev?.toLowerCase();
  const key = Object.keys(BOOK_ABBREV_MAP)?.find(k => k?.toLowerCase() === lower);
  return key ? BOOK_ABBREV_MAP?.[key] : null;
}

function parseVerseRef(ref) {
  if (!ref) return null;
  const m = ref?.trim()?.match(/^([1-3]?[A-Za-z]+)[\.\s]+(\d+)(?::(\d+))?$/);
  if (!m) return null;
  const bookAbbrev = m?.[1];
  const chapter = parseInt(m?.[2], 10);
  const verse = m?.[3] ? parseInt(m?.[3], 10) : null;
  const book = resolveBookName(bookAbbrev);
  if (!book) return null;
  return { book, chapter, verse };
}

function cleanLexiconText(text) {
  if (!text) return text;
  let cleaned = text?.replace(/[A-Za-z0-9][A-Za-z0-9.:]*'>/g, '');
  cleaned = cleaned?.replace(/<a\b[^>]*>/gi, '');
  cleaned = cleaned?.replace(/<\/a>/gi, '');
  return cleaned;
}

function splitIntoSegments(text) {
  if (!text) return [{ type: 'text', value: '' }];

  const cleanedText = cleanLexiconText(text);
  const segments = [];
  const refs = [];

  const re = /\b([1-3]?[A-Za-z]{2,4})\.(\d+)(?:[.:](\d+))?/g;
  let match;
  while ((match = re?.exec(cleanedText)) !== null) {
    const bookAbbrev = match?.[1];
    const book = resolveBookName(bookAbbrev);
    if (book) {
      refs?.push({
        start: match?.index,
        end: match?.index + match?.[0]?.length,
        raw: match?.[0],
        book,
        chapter: parseInt(match?.[2], 10),
        verse: match?.[3] ? parseInt(match?.[3], 10) : null,
      });
    }
  }

  if (refs?.length === 0) {
    return [{ type: 'text', value: cleanedText }];
  }

  let pos = 0;
  for (const ref of refs) {
    if (ref?.start > pos) {
      segments?.push({ type: 'text', value: cleanedText?.slice(pos, ref?.start) });
    }
    segments?.push({ type: 'verse', value: ref?.raw, book: ref?.book, chapter: ref?.chapter, verse: ref?.verse });
    pos = ref?.end;
  }
  if (pos < cleanedText?.length) {
    segments?.push({ type: 'text', value: cleanedText?.slice(pos) });
  }

  return segments;
}

/**
 * Parses meaning into sections. Each __ marker starts a new block.
 */
function parseMeaningBlocks(raw) {
  if (!raw) return [];

  const blocks = [];
  const parts = raw?.split(/(?=__)/);

  for (const part of parts) {
    if (!part?.trim()) continue;

    const underscoreMatch = part?.match(/^(_+)/);
    const underscoreCount = underscoreMatch ? underscoreMatch?.[1]?.length : 0;
    const indentLevel = Math.floor(underscoreCount / 2);
    const content = part?.slice(underscoreCount)?.trim();

    if (!content) continue;

    const isSectionHeader = indentLevel === 0 && /^(I{1,3}V?|VI{0,3}|IX|X{1,3}|[1-9]\d*)\.\s/?.test(content);

    blocks?.push({ indent: indentLevel, html: content, isSectionHeader });
  }

  return blocks;
}

function groupIntoSections(blocks) {
  const sections = [];
  let currentSection = null;

  for (const block of blocks) {
    if (block?.isSectionHeader) {
      if (currentSection) sections?.push(currentSection);
      currentSection = { header: block, children: [] };
    } else if (currentSection) {
      currentSection?.children?.push(block);
    } else {
      sections?.push({ header: null, children: [block] });
    }
  }
  if (currentSection) sections?.push(currentSection);
  return sections;
}

function TextWithVerseLinks({ text, onVerseClick }) {
  const segments = splitIntoSegments(text);

  return (
    <>
      {segments?.map((seg, i) => {
        if (seg?.type === 'verse') {
          return (
            <button
              key={i}
              onClick={() => onVerseClick(seg?.book, seg?.chapter, seg?.verse)}
              className="inline-flex items-center text-primary hover:text-primary/80 hover:underline font-medium cursor-pointer transition-colors touch-manipulation min-h-[36px] sm:min-h-0 sm:py-0"
              title={`Go to ${seg?.book} ${seg?.chapter}${seg?.verse ? ':' + seg?.verse : ''}`}
            >
              {seg?.value}
            </button>
          );
        }
        return (
          <span
            key={i}
            dangerouslySetInnerHTML={{ __html: seg?.value }}
          />
        );
      })}
    </>
  );
}

function CollapsibleSection({ header, children, onVerseClick, indentColors, defaultOpen = true }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      {header && (
        <button
          onClick={() => setIsOpen(v => !v)}
          className="w-full flex items-center justify-between px-3 py-3 bg-muted hover:bg-muted/80 transition-colors text-left touch-manipulation min-h-[44px]"
        >
          <span className="text-sm font-semibold text-foreground leading-snug flex-1 pr-2">
            <TextWithVerseLinks text={header?.html} onVerseClick={onVerseClick} />
          </span>
          <Icon
            name={isOpen ? 'ChevronUp' : 'ChevronDown'}
            size={16}
            className="flex-shrink-0 text-muted-foreground"
          />
        </button>
      )}
      {isOpen && children?.length > 0 && (
        <div className="px-3 py-2 space-y-1.5">
          {children?.map((block, i) => {
            const clampedIndent = Math.min(block?.indent, indentColors?.length - 1);
            const borderColor = indentColors?.[clampedIndent];
            const paddingLeft = block?.indent * 12;

            return (
              <div
                key={i}
                className="flex items-start gap-2"
                style={{ paddingLeft: `${paddingLeft}px` }}
              >
                {block?.indent > 0 && (
                  <div
                    className={`flex-shrink-0 self-stretch w-0.5 rounded-full ${borderColor?.replace('border-', 'bg-')}`}
                    style={{ minHeight: '1em' }}
                  />
                )}
                <p className={`text-sm leading-relaxed flex-1 ${block?.indent === 0 ? 'text-foreground' : 'text-muted-foreground'}`}>
                  <TextWithVerseLinks text={block?.html} onVerseClick={onVerseClick} />
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MeaningDisplay({ meaning, onVerseClick }) {
  const blocks = parseMeaningBlocks(meaning);

  if (!blocks?.length) return null;

  const indentColors = [
    'border-violet-400',
    'border-blue-300',
    'border-green-300',
    'border-amber-300',
    'border-rose-300',
    'border-border',
  ];

  const sections = groupIntoSections(blocks);
  const hasSections = sections?.some(s => s?.header !== null);

  if (!hasSections) {
    return (
      <div className="space-y-1.5">
        {blocks?.map((block, i) => {
          const clampedIndent = Math.min(block?.indent, indentColors?.length - 1);
          const borderColor = indentColors?.[clampedIndent];
          const paddingLeft = block?.indent * 12;

          return (
            <div
              key={i}
              className="flex items-start gap-2"
              style={{ paddingLeft: `${paddingLeft}px` }}
            >
              {block?.indent > 0 && (
                <div
                  className={`flex-shrink-0 self-stretch w-0.5 rounded-full ${borderColor?.replace('border-', 'bg-')}`}
                  style={{ minHeight: '1em' }}
                />
              )}
              <p className={`text-sm leading-relaxed flex-1 ${block?.indent === 0 ? 'text-foreground' : 'text-muted-foreground'}`}>
                <TextWithVerseLinks text={block?.html} onVerseClick={onVerseClick} />
              </p>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {sections?.map((section, i) => (
        <CollapsibleSection
          key={i}
          header={section?.header}
          children={section?.children}
          onVerseClick={onVerseClick}
          indentColors={indentColors}
          defaultOpen={i === 0}
        />
      ))}
    </div>
  );
}

function TraditionalStrongsContent({ strongsNumber, onVerseClick }) {
  const [definition, setDefinition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!strongsNumber) return;
    const fetchTraditional = async () => {
      setLoading(true);
      setError(null);
      try {
        const cleanNumber = strongsNumber?.replace(/[{}]/g, '')?.replace(/_[A-Za-z]$/, '');
        // Strip disambiguation suffix (trailing uppercase letter after digits)
        const baseNumber = cleanNumber?.replace(/^([HG]\d+)[A-Z]$/, '$1');
        // Traditional Strong's uses non-padded numbers (H216, not H0216)
        const lookupNumber = stripLeadingZerosForTraditional(baseNumber);
        const { data, error: dbError } = await supabase
          ?.from('strongs_definitions')
          ?.select('*')
          ?.eq('number', lookupNumber);
        if (dbError) throw dbError;
        setDefinition(data?.[0] || null);
      } catch (err) {
        console.error('Traditional Strong\'s fetch error:', err);
        setError('Failed to load Traditional Strong\'s entry.');
      } finally {
        setLoading(false);
      }
    };
    fetchTraditional();
  }, [strongsNumber]);

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-sm">Loading Traditional Strong's...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 py-6 text-destructive text-sm">
        <Icon name="AlertCircle" size={16} />
        <span>{error}</span>
      </div>
    );
  }

  if (!definition) {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
        <Icon name="BookOpen" size={24} />
        <span className="text-sm">No Traditional Strong's entry found.</span>
      </div>
    );
  }

  const { number, rootWord, transliteration, pronunciation, entry, tvm } = definition;

  const renderHTML = (html) => (
    <span dangerouslySetInnerHTML={{ __html: html || '' }} />
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-xl font-bold text-foreground">{number}</span>
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted px-2 py-0.5 rounded">
          Traditional Strong's
        </span>
      </div>

      {(rootWord || transliteration || pronunciation) && (
        <div className="text-sm text-foreground">
          <div className="flex flex-wrap items-center gap-1.5">
            {rootWord && (
              <span className="font-semibold text-base">
                {renderHTML(rootWord)}
              </span>
            )}
            {(rootWord && (transliteration || pronunciation)) && (
              <span className="text-border">|</span>
            )}
            {transliteration && (
              <span className="italic text-muted-foreground">
                {renderHTML(transliteration)}
              </span>
            )}
            {(transliteration && pronunciation) && (
              <span className="text-border">|</span>
            )}
            {pronunciation && (
              <span className="text-muted-foreground">
                {renderHTML(pronunciation)}
              </span>
            )}
          </div>
        </div>
      )}

      {entry && (
        <div className="text-sm text-foreground leading-relaxed">
          <MeaningDisplay meaning={entry} onVerseClick={onVerseClick} />
        </div>
      )}

      {tvm && (
        <div className="text-sm text-foreground leading-relaxed border-t border-border pt-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Tense / Voice / Mood</p>
          <MeaningDisplay meaning={tvm} onVerseClick={onVerseClick} />
        </div>
      )}
    </div>
  );
}

const LexiconModal = ({ strongsNumber, onClose }) => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSuffixInfo, setShowSuffixInfo] = useState(false);
  const [activeTab, setActiveTab] = useState('stepbible');

  const { eStrongKey, disambigSuffix, instanceSuffix } = parseStrongsNumber(strongsNumber);
  const isHebrew = eStrongKey?.startsWith('H');
  const tableName = isHebrew ? 'lexicon_hebrew' : 'lexicon_greek';
  const wordField = isHebrew ? 'hebrew_form' : 'greek_form';

  // For STEPBible, Hebrew numbers need zero-padding (H216 → H0216)
  const stepbibleKey = padStrongsForSTEP(eStrongKey);

  const originalNumber = strongsNumber?.replace(/_[A-Za-z]$/, '') || '';

  useEffect(() => {
    if (!eStrongKey) return;

    const fetchLexicon = async () => {
      setLoading(true);
      setError(null);
      try {
        const selectFields = `id, e_strong, d_strong, u_strong, transliteration, morph, gloss, meaning, ${wordField}`;
        // Use zero-padded key for STEPBible e_strong column
        const { data, error: dbError } = await supabase
          ?.from(tableName)
          ?.select(selectFields)
          ?.eq('e_strong', stepbibleKey)
          ?.order('d_strong', { ascending: true });

        if (dbError) throw dbError;
        setEntries(data || []);
      } catch (err) {
        console.error('Lexicon fetch error:', err);
        setError('Failed to load lexicon entry.');
      } finally {
        setLoading(false);
      }
    };

    fetchLexicon();
  }, [eStrongKey, tableName, wordField, stepbibleKey]);

  const suffixExplanation = getSuffixExplanation(disambigSuffix, isHebrew);

  const handleVerseClick = (book, chapter, verse) => {
    if (!book) return;
    try {
      const state = { book, chapter, verse };
      localStorage.setItem('bibleReaderNavTarget', JSON.stringify(state));
    } catch { /* ignore */ }
    const params = new URLSearchParams({ book, chapter: String(chapter) });
    if (verse) params?.set('verse', String(verse));
    onClose?.();
    navigate(`/bible-reader?${params?.toString()}`);
  };

  const tabs = [
    { id: 'stepbible', label: 'STEPBible Strongs' },
    { id: 'traditional', label: "Traditional Strong\'s" },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/40"
        onClick={onClose}
      />
      {/* Modal — full-screen on mobile, constrained on larger screens */}
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 pointer-events-none">
        <div className="bg-card w-full sm:rounded-xl shadow-2xl sm:max-w-lg flex flex-col pointer-events-auto
                        rounded-t-2xl max-h-[92vh] sm:max-h-[85vh] border border-border">

          {/* Mobile drag handle */}
          <div className="flex justify-center pt-3 pb-1 sm:hidden">
            <div className="w-10 h-1 rounded-full bg-border" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-border">
            <div className="flex items-center gap-2 flex-wrap">
              <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isHebrew ? 'bg-violet-500' : 'bg-primary'}`} />
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {isHebrew ? 'Hebrew' : 'Greek'} Lexicon
              </span>
              {disambigSuffix ? (
                <span className="text-base font-bold text-foreground">
                  {eStrongKey}
                  <span className="text-amber-500">{disambigSuffix}</span>
                </span>
              ) : (
                <span className="ml-1 text-base font-bold text-foreground">{eStrongKey}</span>
              )}
              {disambigSuffix && (
                <button
                  onClick={() => setShowSuffixInfo(v => !v)}
                  className="flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 transition-colors touch-manipulation min-h-[32px]"
                  title="Why is there a suffix?"
                >
                  <Icon name="Info" size={10} />
                  Why "{disambigSuffix}"?
                </button>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors rounded-full p-2 hover:bg-muted flex-shrink-0 touch-manipulation min-w-[40px] min-h-[40px] flex items-center justify-center"
              aria-label="Close lexicon"
            >
              <Icon name="X" size={18} />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-border px-4 sm:px-5">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`py-3 px-1 mr-5 text-sm font-medium border-b-2 transition-colors touch-manipulation min-h-[44px] ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                {tab?.label}
              </button>
            ))}
          </div>

          {/* Suffix explanation banner */}
          {activeTab === 'stepbible' && disambigSuffix && showSuffixInfo && (
            <div className="mx-4 sm:mx-5 mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 flex gap-2">
              <Icon name="Info" size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-foreground leading-relaxed">
                <p className="font-semibold mb-1">
                  About the suffix &ldquo;<span className="text-amber-500">{disambigSuffix}</span>&rdquo; in {originalNumber}:
                </p>
                <p className="text-muted-foreground">{suffixExplanation}</p>
                <p className="mt-1.5 text-amber-500">
                  Showing all entries for base number <strong>{eStrongKey}</strong> below.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'stepbible' && disambigSuffix && !showSuffixInfo && (
            <div className="mx-4 sm:mx-5 mt-3 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center gap-2">
              <Icon name="Search" size={12} className="text-amber-500 flex-shrink-0" />
              <p className="text-[11px] text-amber-500">
                Looking up base number <strong>{eStrongKey}</strong> (suffix <strong className="text-amber-400">{disambigSuffix}</strong> stripped).{' '}
                <button
                  onClick={() => setShowSuffixInfo(true)}
                  className="underline hover:text-amber-400"
                >
                  Why?
                </button>
              </p>
            </div>
          )}

          {/* Body — single column, scrollable */}
          <div className="overflow-y-auto flex-1 px-4 sm:px-5 py-4">

            {/* STEPBible Tab */}
            {activeTab === 'stepbible' && (
              <>
                {loading && (
                  <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm">Loading lexicon...</span>
                  </div>
                )}

                {!loading && error && (
                  <div className="flex items-center gap-2 py-6 text-destructive text-sm">
                    <Icon name="AlertCircle" size={16} />
                    <span>{error}</span>
                  </div>
                )}

                {!loading && !error && entries?.length === 0 && (
                  <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
                    <Icon name="BookOpen" size={24} />
                    <span className="text-sm">No lexicon entry found for {eStrongKey}.</span>
                  </div>
                )}

                {!loading && !error && entries?.length > 0 && (
                  <div className="space-y-5">
                    {entries?.map((entry, idx) => (
                      <div
                        key={entry?.id || idx}
                        className={entries?.length > 1 ? 'border border-border rounded-lg p-4 bg-muted/30' : ''}
                      >
                        {/* Word form + transliteration + gloss — stacked on mobile */}
                        <div className="flex flex-col gap-1 mb-3">
                          <div className="flex flex-wrap items-baseline gap-2">
                            {entry?.[wordField] && (
                              <span
                                className={`font-bold text-foreground ${isHebrew ? 'text-2xl' : 'text-xl'}`}
                                dir={isHebrew ? 'rtl' : 'ltr'}
                                style={isHebrew ? { fontFamily: 'serif' } : {}}
                              >
                                {entry?.[wordField]}
                              </span>
                            )}
                            {entry?.transliteration && (
                              <span className="text-sm italic text-muted-foreground">
                                {entry?.transliteration}
                              </span>
                            )}
                          </div>
                          {entry?.gloss && (
                            <span className="text-sm font-semibold text-primary">
                              &ldquo;{entry?.gloss}&rdquo;
                            </span>
                          )}
                        </div>

                        {/* d_strong + morph badges */}
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {entry?.d_strong && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-primary/20 text-primary">
                              {entry?.d_strong}
                            </span>
                          )}
                          {entry?.morph && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono bg-muted text-muted-foreground">
                              {entry?.morph}
                            </span>
                          )}
                        </div>

                        {/* Meaning with collapsible sections and verse links */}
                        {entry?.meaning && (
                          <MeaningDisplay meaning={entry?.meaning} onVerseClick={handleVerseClick} />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Traditional Strong's Tab */}
            {activeTab === 'traditional' && (
              <TraditionalStrongsContent strongsNumber={strongsNumber} onVerseClick={handleVerseClick} />
            )}
          </div>

          {/* Footer */}
          <div className="px-4 sm:px-5 py-3 border-t border-border bg-muted/30 rounded-b-xl">
            {activeTab === 'stepbible' ? (
              <p className="text-[10px] text-muted-foreground">
                Data: STEPBible.org / Tyndale House Cambridge (CC BY 4.0) · Verse references are clickable links
              </p>
            ) : (
              <p className="text-[10px] text-muted-foreground">
                Traditional Strong's Concordance · Verse references are clickable links
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default LexiconModal;
